// ============================================
// Learning Info Modal Component
// ============================================

import { Modal, ModalFooter, Button } from '@/components/ui';

interface LearningInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function LearningInfoModal({ isOpen, onClose }: LearningInfoModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="How Learning Works" size="lg">
      <div className="space-y-6 text-gray-700">
        {/* Adaptive Learning */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <span aria-hidden="true">&#x1F3AF;</span> Adaptive Learning System
          </h3>
          <p>
            This app uses <strong>spaced repetition</strong> to optimize your learning.
            Words you find difficult appear more frequently, while mastered words appear less often.
          </p>
        </section>

        {/* Learning States — the path to Mastered */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <span aria-hidden="true">&#x1F504;</span> Getting a word to Mastered
          </h3>
          <p className="text-sm mb-3">
            Every word climbs a ladder. Each time you answer it <strong>correctly</strong> it moves
            up a rung and won&rsquo;t come back for longer. Miss it and it slips down a rung, so you
            see it again sooner. Climb all the way and it&rsquo;s <strong>Mastered</strong>.
          </p>
          {/* Visual path N → L → R → M */}
          <div className="flex items-center gap-1.5 mb-3" aria-hidden="true">
            <PathDot indicator="N" colorClass="bg-srs-new" textColor="#1F2A3A" />
            <span className="text-gray-400">&rarr;</span>
            <PathDot indicator="L" colorClass="bg-srs-learning" textColor="#1F2A3A" />
            <span className="text-gray-400">&rarr;</span>
            <PathDot indicator="R" colorClass="bg-srs-review" textColor="#FFFFFF" />
            <span className="text-gray-400">&rarr;</span>
            <PathDot indicator="M" colorClass="bg-srs-mastered" textColor="#1F2A3A" />
          </div>
          <div className="grid gap-2">
            <StateIndicator
              indicator="N"
              colorClass="bg-srs-new"
              textColor="#1F2A3A"
              label="New"
              description="Haven't studied it yet"
            />
            <StateIndicator
              indicator="L"
              colorClass="bg-srs-learning"
              textColor="#1F2A3A"
              label="Learning"
              description="Just started — comes back in minutes to an hour"
            />
            <StateIndicator
              indicator="R"
              colorClass="bg-srs-review"
              textColor="#FFFFFF"
              label="Review"
              description="Getting solid — comes back in days"
            />
            <StateIndicator
              indicator="M"
              colorClass="bg-srs-mastered"
              textColor="#1F2A3A"
              label="Mastered"
              description="Locked in — comes back about once a month"
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            It takes several correct answers, spaced out over days, to fully master a word — that
            spacing is what makes it stick.
          </p>
        </section>

        {/* Study Mode */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <span aria-hidden="true">&#x1F4DA;</span> Study Mode
          </h3>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>Flip the card to check yourself, then tap <strong>Got it</strong> or <strong>Again</strong></li>
            <li><strong>Got it</strong> moves the word up the ladder; <strong>Again</strong> brings it back sooner</li>
            <li>Tap the audio button to hear the pronunciation</li>
            <li>Finish the set, then tap <strong>Study 10 more</strong> to bring in new words</li>
          </ul>
          <p className="text-xs text-gray-500 mt-2">
            Quizzes count too — every correct answer there moves a word up the same ladder.
          </p>
        </section>

        {/* Quiz Modes */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <span aria-hidden="true">&#x1F9E0;</span> Quiz Modes
          </h3>
          <ul className="text-sm space-y-1">
            <li><strong>Multiple Choice:</strong> Standard Japanese to English</li>
            <li><strong>Listening First:</strong> Audio-based learning</li>
            <li><strong>Mixed Challenge:</strong> Random JP to EN or EN to JP</li>
            <li><strong>Speed Challenge:</strong> 8-second time pressure</li>
            <li><strong>Kanji Only:</strong> Focus on character recognition</li>
            <li><strong>Particle Quiz:</strong> Master Japanese particles</li>
          </ul>
        </section>

        {/* Keyboard Shortcuts */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <span aria-hidden="true">&#x2328;&#xFE0F;</span> Keyboard Shortcuts
          </h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <ShortcutItem keys={['Space', 'Enter']} action="Flip card / Next" />
            <ShortcutItem keys={['Arrow Left', 'Arrow Right']} action="Navigate" />
            <ShortcutItem keys={['1', '2']} action="Again / Got it (study)" />
            <ShortcutItem keys={['1', '2', '3', '4']} action="Select answer (quiz)" />
            <ShortcutItem keys={['K']} action="Toggle kana" />
            <ShortcutItem keys={['Escape']} action="Close modal" />
          </div>
        </section>

        {/* Progress Tracking */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <span aria-hidden="true">&#x1F4CA;</span> Progress Tracking
          </h3>
          <p className="text-sm">
            Your progress is automatically saved per level. The system tracks your performance
            and adjusts review timing to maximize retention.
          </p>
        </section>
      </div>

      <ModalFooter>
        <Button variant="primary" onClick={onClose}>
          Got it!
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ============================================
// Path Dot (compact N → L → R → M visual)
// ============================================

interface PathDotProps {
  indicator: string;
  colorClass: string;
  textColor: string;
}

function PathDot({ indicator, colorClass, textColor }: PathDotProps) {
  return (
    <div
      className={`w-7 h-7 rounded-full ${colorClass} flex items-center justify-center font-bold text-xs`}
      style={{ color: textColor }}
    >
      {indicator}
    </div>
  );
}

// ============================================
// State Indicator Row
// ============================================

interface StateIndicatorProps {
  indicator: string;
  colorClass: string;
  textColor: string;
  label: string;
  description: string;
}

function StateIndicator({ indicator, colorClass, textColor, label, description }: StateIndicatorProps) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={`
          w-8 h-8 rounded-full ${colorClass}
          flex items-center justify-center font-bold text-sm
        `}
        style={{ color: textColor }}
        aria-hidden="true"
      >
        {indicator}
      </div>
      <div>
        <span className="font-medium text-gray-900">{label}</span>
        <span className="text-gray-500 ml-2">- {description}</span>
      </div>
    </div>
  );
}

// ============================================
// Keyboard Shortcut Item
// ============================================

interface ShortcutItemProps {
  keys: string[];
  action: string;
}

function ShortcutItem({ keys, action }: ShortcutItemProps) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-1">
        {keys.map((key, i) => (
          <>
            <kbd key={key} className="px-2 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs font-mono">
              {key}
            </kbd>
            {i < keys.length - 1 && <span className="text-gray-400 mx-0.5">/</span>}
          </>
        ))}
      </div>
      <span className="text-gray-600">{action}</span>
    </div>
  );
}
